import { create } from "zustand";
import {
  UPLOAD_PHASES,
  type ImportConfig,
  type SheetOutline,
} from "Worker/entities";
import type { CanvasPoint } from "components/canvas/canvasPoint";

export type ImportItemStatus =
  | "queued"
  | "importing"
  | "needs_settings"
  | "needs_choice"
  | "done"
  | "failed"
  | "rejected"
  /** A reload took its file: nothing can be resumed, the person drops it again. */
  | "interrupted";

export interface ImportItem {
  id: number;
  /** A dropped file, or the workbook a picked table is read from. A Google
   *  Sheet table has none: it carries `connect` instead. */
  file?: File;
  /** A Google Sheet table: imported by the server through its connection. */
  connect?: () => Promise<void>;
  /** The model this file was dropped into. Carried per item, never read from
   *  the current route: a run outlives the page the user is looking at. The
   *  pump runs only the rows matching `mountedModelId`, because the table is
   *  created under this id but attached by whichever canvas is mounted when
   *  the reply lands — so running one model's files under another splits them. */
  modelId: string;
  name: string;
  /** Where the file was dropped, in canvas coordinates; absent for a button import. */
  at?: CanvasPoint;
  /** Excel only: the sheet and cell range the user picked. */
  sheet?: string;
  range?: string;
  transpose?: boolean;
  /** A workbook waiting for its tables to be picked, as the pump read it. */
  outline?: SheetOutline[];
  size: number;
  status: ImportItemStatus;
  /** Why it stopped — the row carries it, so nothing toasts per file. */
  message?: string;
  /** 0..100 while importing. */
  progress?: number;
  attempts: number;
  /** Already counted in an end-of-run summary; the next run's is not about it. */
  reported?: boolean;
  /** The panel's settings for a CSV it reviewed; absent, the worker sniffs. */
  config?: ImportConfig;
  /** From `start`: the ticket a reload can resume or cancel. */
  uploadId?: string;
  /** Every byte of `uploadId` is with the server: a reload resumes it. */
  landed?: boolean;
  /** No file, only a ticket: the worker asks finalize again. */
  resume?: boolean;
}

/** Stopped and waiting on the person: the chip's to-do list. */
export const isLeftover = (i: ImportItem) =>
  i.status === "needs_settings" ||
  i.status === "needs_choice" ||
  i.status === "failed" ||
  i.status === "rejected" ||
  i.status === "interrupted";

export const IMPORT_QUEUE_STORAGE_KEY = "mycorr.importQueue.v1";

const STATUSES = new Set<ImportItemStatus>([
  "queued",
  "importing",
  "needs_settings",
  "needs_choice",
  "done",
  "failed",
  "rejected",
  "interrupted",
]);

/** The account the rows belong to: a tab keeps its storage across a sign-out. */
function owner(): string | null {
  try {
    return localStorage.getItem("clientID");
  } catch {
    return null;
  }
}

/** Per tab: a reload brings the rows back, another tab never sees them. A
 *  File cannot be stored, so what a reload can do with each row is decided on
 *  the way back in (`restoreImportItems`). */
function saveImportItems(items: ImportItem[]): void {
  try {
    const rows = items.filter((i) => i.status !== "done" && !i.connect);
    sessionStorage.setItem(
      IMPORT_QUEUE_STORAGE_KEY,
      JSON.stringify({ owner: owner(), rows }, (key, value) =>
        key === "file" || key === "connect" || key === "outline"
          ? undefined
          : (value as unknown),
      ),
    );
  } catch {
    // Storage unavailable: the queue lives for this page only.
  }
}

function isRow(row: unknown): row is ImportItem {
  if (typeof row !== "object" || row === null) return false;
  const r = row as Partial<ImportItem>;
  return (
    Number.isFinite(r.id) &&
    typeof r.name === "string" &&
    typeof r.modelId === "string" &&
    STATUSES.has(r.status as ImportItemStatus)
  );
}

/** A row whose bytes landed resumes; any other live row lost its file with
 *  the page. Neither belongs to the next run's summary. */
function revive(row: ImportItem): ImportItem {
  const live =
    row.status === "queued" ||
    row.status === "importing" ||
    row.status === "needs_settings" ||
    row.status === "needs_choice";
  if (!live) return { ...row, resume: false, reported: true };
  if (
    (row.status === "importing" || row.status === "queued") &&
    row.landed &&
    row.uploadId
  ) {
    return {
      ...row,
      status: "queued" as const,
      resume: true,
      progress: undefined,
      message: UPLOAD_PHASES.server,
    };
  }
  return {
    ...row,
    status: "interrupted" as const,
    resume: false,
    reported: true,
    progress: undefined,
    message: "Interrupted by the page reload — drop the file again",
  };
}

export function restoreImportItems(): ImportItem[] {
  let saved: { owner?: unknown; rows?: unknown };
  try {
    saved = JSON.parse(
      sessionStorage.getItem(IMPORT_QUEUE_STORAGE_KEY) ?? "{}",
    );
  } catch {
    return [];
  }
  if (!saved || saved.owner !== owner() || !Array.isArray(saved.rows))
    return [];
  return saved.rows.filter(isRow).map(revive);
}

/** The settings a row's Review hands the panel along with its file. */
let pendingReviewConfig: ImportConfig | null = null;
export function setPendingReviewConfig(config: ImportConfig | undefined): void {
  pendingReviewConfig = config ?? null;
}
export function takePendingReviewConfig(): ImportConfig | null {
  const config = pendingReviewConfig;
  pendingReviewConfig = null;
  return config;
}

/** State only, so the worker-operation hook can ask who owns a filename
 *  without importing the pump (which imports that hook back). */
export const useImportQueueStore = create<{
  items: ImportItem[];
  /** The canvas on screen, so a row can say it belongs to another model
   *  rather than offering a Review that would import into this one. */
  mountedModelId: string | null;
  /** The upload panel holds a file; Review would replace it, so it waits. */
  panelFileOpen: boolean;
}>(() => ({
  items: [],
  mountedModelId: null,
  panelFileOpen: false,
}));

export const setImportPanelFileOpen = (open: boolean) =>
  useImportQueueStore.setState({ panelFileOpen: open });

export const setImportItems = (fn: (items: ImportItem[]) => ImportItem[]) =>
  useImportQueueStore.setState((s) => ({ items: fn(s.items) }));

export const patchImportItem = (id: number, next: Partial<ImportItem>) =>
  setImportItems((items) =>
    items.map((i) => (i.id === id ? { ...i, ...next } : i)),
  );

/** True while the queue is answering for this file: it reports the outcome in
 *  the file's own row, so the shared upload toast must stay quiet. Only the row
 *  actually in flight can claim a name — the worker protocol keys replies by
 *  filename alone, so the narrower the claim, the fewer messages we can steal
 *  from an unrelated import of a same-named file. */
export function importQueueOwns(filename: string): boolean {
  return importQueueRow(filename) !== undefined;
}

/** The queue row importing `filename` right now, if the queue owns it. */
export function importQueueRow(filename: string): ImportItem | undefined {
  return useImportQueueStore
    .getState()
    .items.find((i) => i.name === filename && i.status === "importing");
}

/** Anything that still needs the panel — work to run, or a decision to take.
 *  A failed or rejected row wants nothing from it, so one left un-dismissed
 *  must not pin every later import to this tab. */
/** A file the page would lose on leaving: the leave-page guard asks. */
export function importQueueBusy(): boolean {
  return useImportQueueStore
    .getState()
    .items.some((i) => i.status === "queued" || i.status === "importing");
}

useImportQueueStore.subscribe((s) => saveImportItems(s.items));
